/**
 * Photo Upload Module for InterPals Private Messages
 * Handles file validation, preview, upload, and progress tracking
 *
 * Uses shared core from photo-upload-core.js for validation and upload logic
 */

import {
  PhotoUploadConfig as CoreConfig,
  PhotoUploadErrors as CoreErrors,
  validateFile,
  uploadPhoto as coreUploadPhoto
} from '../modules/photo-upload-core.js';
import { t } from '../modules/i18n.js';

// Extended config for PM-specific UI settings
const PhotoUploadConfig = {
  ...CoreConfig,
  PREVIEW_MAX_HEIGHT: 128, // max-h-32 = 128px
  SCROLL_DELAY: 200,
  FOCUS_DELAY: 200,
  PREVIEW_SIZE: '300x400' // nginx allowed size
};

// Extended errors for PM-specific messages
const PhotoUploadErrors = {
  ...CoreErrors,
  get SEND_FAILED() { return t('common.photo_send_failed'); },
  get UPLOAD_IN_PROGRESS() { return t('common.photo_upload_in_progress'); },
  get FILE_READ_ERROR() { return t('common.photo_file_read_error'); }
};

/**
 * File validator utility class (wraps core validateFile)
 */
class FileValidator {
  static validate(file) {
    if (!file) {
      return [];
    }
    const result = validateFile(file);
    return result.valid ? [] : [result.error];
  }
}

/**
 * Progress tracker for upload operations
 */
class UploadProgressTracker {
  constructor(elements) {
    this.elements = elements;
    this.isVisible = false;
  }

  show() {
    this.elements.photoUploadProgress.removeClass('hidden');
    this.elements.photoProgressBar.css('width', '0%');
    this.elements.uploadStatusText.text(t('common.photo_uploading'));
    this.elements.photoUploadProgress.attr('aria-valuenow', '0');
    this.isVisible = true;
  }

  hide() {
    this.elements.photoUploadProgress.addClass('hidden');
    this.elements.photoUploadProgress.removeAttr('aria-valuenow');
    this.isVisible = false;
  }

  updateProgress(percentage) {
    if (!this.isVisible) return;
    
    const roundedPercentage = Math.round(percentage);
    this.elements.photoProgressBar.css('width', percentage + '%');
    this.elements.photoUploadProgress.attr('aria-valuenow', roundedPercentage);
    
    if (percentage >= 100) {
      this.elements.uploadStatusText.text(t('common.photo_processing'));
    }
  }

  setError(message) {
    if (this.isVisible) {
      this.elements.uploadStatusText.text(t('common.photo_upload_failed'));
    }
  }

  setComplete() {
    if (this.isVisible) {
      this.elements.uploadStatusText.text(t('common.photo_upload_complete'));
    }
  }
}

/**
 * Photo preview manager
 */
class PhotoPreview {
  constructor(elements) {
    this.elements = elements;
  }

  show(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        this.elements.photoPreview.attr('src', e.target.result);
        this.elements.photoPreviewContainer.removeClass('hidden');
        resolve();
      };
      
      reader.onerror = () => {
        reject(new Error(PhotoUploadErrors.FILE_READ_ERROR));
      };
      
      reader.readAsDataURL(file);
    });
  }

  hide() {
    this.elements.photoPreview.attr('src', '');
    this.elements.photoPreviewContainer.addClass('hidden');
  }

}

/**
 * Upload service for handling server communication
 * Wraps core upload function with jQuery event integration
 */
class PhotoUploadService {
  static async upload(file) {
    const result = await coreUploadPhoto({
      file,
      onProgress: (percent) => {
        $(document).trigger('photo-upload-progress', percent);
      }
    });
    return result.id;
  }
}

/**
 * Main photo upload manager
 */
class PhotoUploadManager {
  constructor() {
    this.selectedFile = null;
    this.photoId = null;
    this.isUploading = false;
    this.elements = {};
    this.progressTracker = null;
    this.preview = null;
    
    this.init();
  }

  init() {
    this.cacheElements();
    this.initializeComponents();
    this.bindEvents();
    this.updateCameraButtonVisibility();
  }

  cacheElements() {
    this.elements = {
      photoInput: $('#photo_input'),
      photoPreview: $('#photo_preview'),
      photoPreviewContainer: $('#photo_preview_container'),
      removePhotoBtn: $('#remove_photo'),
      photoId: $('#photo_id'),
      photoUploadProgress: $('#photo_upload_progress'),
      photoProgressBar: $('#photo_progress_bar'),
      uploadStatusText: $('#upload_status_text'),
      messageTextarea: $('#message'),
      cameraBtn: $('#camera_btn'),
      msgSubmit: $('#msg_submit'),
      sendButtonText: $('#send_button_text')
    };
  }

  initializeComponents() {
    this.progressTracker = new UploadProgressTracker(this.elements);
    this.preview = new PhotoPreview(this.elements);
  }

  bindEvents() {
    // File selection
    this.elements.photoInput.on('change', this.handleFileSelection.bind(this));
    
    // Remove photo
    this.elements.removePhotoBtn.on('click', this.removePhoto.bind(this));
    
    // Message textarea changes
    this.elements.messageTextarea.on('input', this.updateCameraButtonVisibility.bind(this));

    // Clipboard paste image support
    this.elements.messageTextarea.on('paste', this.handlePaste.bind(this));
    
    // Progress updates
    $(document).on('photo-upload-progress', (event, percentage) => {
      this.progressTracker.updateProgress(percentage);
    });
    
    // Clean up on thread close
    $(document).on('thread_closed', this.removePhoto.bind(this));
  }

  async handleFileSelection(event) {
    const file = event.target.files[0];
    if (!file) return;

    await this.handleFile(file);

    // Clear file input if validation failed
    if (!this.selectedFile || this.selectedFile !== file) {
      this.elements.photoInput.val('');
    }
  }

  async handleFile(file) {
    if (!file) return;

    try {
      this.validateFile(file);

      if (this.selectedFile) {
        this.removePhoto();
      }

      this.selectedFile = file;
      await this.preview.show(file);
      this.updateCameraButtonVisibility();
      this.updateSendButtonState();
    } catch (error) {
      this.showError(error.message);
    }
  }

  handlePaste(event) {
    const clipboardData = event.clipboardData || event.originalEvent?.clipboardData;
    if (!clipboardData || !clipboardData.items) return;

    const imageItem = Array.from(clipboardData.items).find(
      item => item.kind === 'file' && item.type.startsWith('image/')
    );

    if (!imageItem) return;

    event.preventDefault();

    const file = imageItem.getAsFile();
    if (!file) return;

    this.handleFile(file);
  }

  validateFile(file) {
    const errors = FileValidator.validate(file);
    if (errors.length > 0) {
      throw new Error(errors[0]); // Show first error
    }
  }

  async uploadPhoto(file) {
    if (this.isUploading) {
      throw new Error(PhotoUploadErrors.UPLOAD_IN_PROGRESS);
    }

    this.isUploading = true;
    this.progressTracker.show();

    try {
      this.photoId = await PhotoUploadService.upload(file);
      this.photoId = String(this.photoId);
      this.progressTracker.setComplete();
      return this.photoId;
    } catch (error) {
      this.progressTracker.setError(error.message);
      throw error;
    } finally {
      this.isUploading = false;
      setTimeout(() => {
        this.progressTracker.hide();
      }, 1000); // Keep visible briefly to show completion
    }
  }

  updateCameraButtonVisibility() {
    const hasText = this.elements.messageTextarea.val().trim().length > 0;
    const hasPhoto = this.selectedFile !== null;

    if (hasText && !hasPhoto) {
      this.elements.cameraBtn.hide();
    } else {
      this.elements.cameraBtn.show();
    }
  }

  removePhoto() {
    // Reset form elements
    this.elements.photoInput.val('');
    this.elements.photoId.val('');

    // Hide preview
    this.preview.hide();

    // Hide progress
    this.progressTracker.hide();

    // Reset state
    this.selectedFile = null;
    this.photoId = null;
    this.isUploading = false;

    this.updateCameraButtonVisibility();
    this.updateSendButtonState();
  }

  updateSendButtonState() {
    if (typeof window.updateSendButtonState === 'function') {
      window.updateSendButtonState();
    }
  }

  showError(message) {
    // TODO: Replace with better notification system
    // For now, use alert but add delay to prevent blocking UI
    setTimeout(() => {
      alert(message);
    }, 100);
  }

  // Public API
  getSelectedFile() {
    return this.selectedFile;
  }

  getPhotoId() {
    return this.photoId;
  }

  hasPhoto() {
    return this.selectedFile !== null || this.photoId !== null;
  }

  isUploadInProgress() {
    return this.isUploading;
  }
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    PhotoUploadManager,
    PhotoUploadConfig,
    PhotoUploadErrors,
    FileValidator,
    PhotoUploadService
  };
}

// Global initialization function for backward compatibility
function initPhotoUpload() {
  const photoUploadManager = new PhotoUploadManager();

  // Expose API for backward compatibility and external use
  window.photoUpload = {
    uploadPhoto: photoUploadManager.uploadPhoto.bind(photoUploadManager),
    getSelectedFile: photoUploadManager.getSelectedFile.bind(photoUploadManager),
    getPhotoId: photoUploadManager.getPhotoId.bind(photoUploadManager),
    removePhoto: photoUploadManager.removePhoto.bind(photoUploadManager),
    hasPhoto: photoUploadManager.hasPhoto.bind(photoUploadManager),
    isUploadInProgress: photoUploadManager.isUploadInProgress.bind(photoUploadManager),
    handleFile: photoUploadManager.handleFile.bind(photoUploadManager)
  };

  return photoUploadManager;
}

// Auto-initialize if jQuery is available
if (typeof $ !== 'undefined') {
  $(document).ready(() => {
    if ($(".privateMessages").length > 0) {
      initPhotoUpload();
    }
  });
} 